/** @format */

import {ApolloClient, HttpLink, InMemoryCache, type NormalizedCacheObject} from '@apollo/client';
import {onError} from '@apollo/client/link/error';
import {setContext} from '@apollo/client/link/context';
import * as Sentry from '@sentry/react';

/**
 * Ключ гостевого токена в `sessionStorage`.
 *
 * Слаг внутри ключа, а не рядом: две вкладки с разными публичными
 * ссылками не должны обмениваться доступом.
 */
export const publicReportTokenKey = (slug: string) => `PUBLIC_REPORT_TOKEN:${slug}`;

/**
 * Доступ к `sessionStorage` через попытку, а не напрямую.
 *
 * Пересланную ссылку на отчёт открывают именно там, где хранилище может
 * быть запрещено: приватное окно, кросс-сайтовый iframe, браузер с
 * отключёнными данными сайта. В этих условиях обращение не возвращает
 * `null`, а **бросает** — и бросок внутри Apollo-линка уронил бы каждую
 * операцию маршрута, оставив гостя с сообщением про соединение и без
 * единого намёка на причину.
 */
export function readPublicReportToken(slug: string): string | null {
  try {
    return sessionStorage.getItem(publicReportTokenKey(slug));
  } catch {
    return null;
  }
}

/** Вернёт `false`, если хранилище запрещено: вызывающий обязан это сказать гостю. */
export function writePublicReportToken(slug: string, token: string): boolean {
  try {
    sessionStorage.setItem(publicReportTokenKey(slug), token);
    return true;
  } catch {
    return false;
  }
}

/**
 * Забыть токен.
 *
 * Нужно на транспортном отказе: негодный токен, отправляемый снова и снова,
 * запирает вкладку навсегда — перезагрузка воспроизводит тот же отказ, и
 * форма пароля становится недостижимой.
 */
export function forgetPublicReportToken(slug: string): void {
  try {
    sessionStorage.removeItem(publicReportTokenKey(slug));
  } catch {
    // Хранилище запрещено — забывать нечего.
  }
}

/**
 * Отдельный клиент публичного маршрута.
 *
 * Изоляция здесь структурная, а не дисциплинарная: этот код не имеет
 * доступа ни к `Bearer` из `localStorage`, ни к `errorLink`, который на
 * строку `unauthenticated` стирает сессию и перезагружает страницу. У
 * гостя, которому стирать нечего, та перезагрузка дала бы бесконечный
 * цикл — запрос, ошибка, перезагрузка, запрос.
 *
 * Фабрика, а не готовый экземпляр: токен лежит под ключом со слагом, а
 * модуль, исполненный на импорте, слага не знает. Читать слаг внутри
 * линка из `window.location.pathname` значило бы завести второй разбор
 * маршрута, который разойдётся с `ROUTES` при первом переименовании.
 */
export function createPublicApolloClient(slug: string): ApolloClient<NormalizedCacheObject> {
  const tokenLink = setContext((_, {headers}) => {
    const token = readPublicReportToken(slug);
    // Токена нет — заголовка нет вовсе, а не пустая строка: первый запрос
    // по адресу обязан уйти без него.
    if (!token) return {headers};

    return {headers: {...headers, 'Public-Report-Token': token}};
  });

  /**
   * Отчётность в Sentry — но без стирания сессии и без перезагрузки.
   *
   * Отказ этого маршрута иначе не виден никому: у его пользователей нет ни
   * аккаунта, ни канала поддержки, чтобы о нём сообщить. Выкидывая
   * `errorLink` целиком, мы выкинули заодно и наблюдаемость, хотя мешала
   * только перезагрузка.
   *
   * Переменные операции сюда не попадают намеренно: у
   * `openPublicProjectReport` в них лежит пароль от ссылки.
   */
  const reportingLink = onError(({graphQLErrors, networkError, operation}) => {
    const tags = {surface: 'public-report', operation: operation.operationName};

    if (networkError) {
      Sentry.captureException(networkError, {tags});
    }

    graphQLErrors?.forEach(({message}) => {
      Sentry.captureMessage(message, {tags});
    });
  });

  const httpLink = new HttpLink({
    // То же выражение, что у основного клиента (`src/index.jsx`). Адрес
    // `/api/graphql` живёт только в `codegen.ts` и для рантайма неверен.
    uri: new URL('api', import.meta.env.VITE_API_URI).toString(),
    credentials: 'omit',
  });

  return new ApolloClient({
    link: tokenLink.concat(reportingLink).concat(httpLink),
    cache: new InMemoryCache(),
    defaultOptions: {watchQuery: {fetchPolicy: 'no-cache'}},
  });
}
